import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  reconciliationApi,
  DaySummary,
  CashierSession,
  MethodBreakdownRow,
  StreamMethodBreakdown,
} from "@/api/reconciliation";
import { format } from "date-fns";
import { Wallet, Loader2 } from "lucide-react";
import { toast } from "sonner";

function formatInr(n: number | undefined) {
  const v = typeof n === "number" && !Number.isNaN(n) ? n : 0;
  return `₹${v.toFixed(2)}`;
}

function methodLabel(method: string) {
  const map: Record<string, string> = {
    CASH: "Cash",
    UPI: "UPI",
    CARD: "Card",
    BANK_TRANSFER: "Bank transfer",
    CHEQUE: "Cheque",
    INSURANCE: "Insurance",
    ONLINE: "Online",
  };
  return map[method] ?? method.replace(/_/g, " ");
}

function MethodTable({ rows, emptyHint }: { rows: MethodBreakdownRow[]; emptyHint?: string }) {
  if (!rows.length) {
    return <p className="text-sm text-muted-foreground py-2">{emptyHint ?? "No activity for this day."}</p>;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Method</TableHead>
          <TableHead className="text-right">Received</TableHead>
          <TableHead className="text-right">Refunded</TableHead>
          <TableHead className="text-right">Net</TableHead>
          <TableHead className="text-right">Txns</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((m) => {
          const net = m.received - m.refunded;
          return (
            <TableRow key={m.method}>
              <TableCell>{methodLabel(m.method)}</TableCell>
              <TableCell className="text-right tabular-nums">{formatInr(m.received)}</TableCell>
              <TableCell className="text-right tabular-nums text-amber-700">{formatInr(m.refunded)}</TableCell>
              <TableCell className="text-right tabular-nums font-medium">{formatInr(net)}</TableCell>
              <TableCell className="text-right tabular-nums">{m.count}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

const STREAM_LABELS: Record<keyof StreamMethodBreakdown, string> = {
  OPD: "OPD",
  LAB: "Lab",
  DAY_CARE: "Day care",
  ADDITIONAL_SERVICES: "Additional Services",
  IPD: "IPD",
};

export default function ReconciliationPage() {
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [closeId, setCloseId] = useState<string | null>(null);
  const [actualCash, setActualCash] = useState("");
  const [closeNotes, setCloseNotes] = useState("");
  const queryClient = useQueryClient();

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ["reconciliation-summary", date],
    queryFn: async () => {
      const res = await reconciliationApi.getDaySummary(date);
      return res.data?.data as DaySummary;
    },
  });

  const { data: sessions = [] } = useQuery({
    queryKey: ["reconciliation-sessions", date],
    queryFn: async () => {
      const res = await reconciliationApi.getSessions(date, date);
      return (res.data?.data ?? []) as CashierSession[];
    },
  });

  const openMutation = useMutation({
    mutationFn: () => reconciliationApi.openSession(date),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reconciliation-sessions", date] });
      queryClient.invalidateQueries({ queryKey: ["reconciliation-summary", date] });
      toast.success("Session opened");
    },
    onError: (e: { response?: { data?: { message?: string } } }) => {
      toast.error(e.response?.data?.message ?? "Failed to open session");
    },
  });

  const closeMutation = useMutation({
    mutationFn: (id: string) =>
      reconciliationApi.closeSession(id, {
        actualCash: parseFloat(actualCash) || 0,
        notes: closeNotes || undefined,
      }),
    onSuccess: () => {
      setCloseId(null);
      setActualCash("");
      setCloseNotes("");
      queryClient.invalidateQueries({ queryKey: ["reconciliation-sessions", date] });
      toast.success("Session closed");
    },
    onError: (e: { response?: { data?: { message?: string } } }) => {
      toast.error(e.response?.data?.message ?? "Failed to close session");
    },
  });

  const sessionForDate = sessions[0];
  const canOpen = !sessionForDate && !openMutation.isPending;
  const canClose = sessionForDate && !sessionForDate.closedAt && !closeMutation.isPending;

  const gen = summary?.generated;
  const col = summary?.collected;
  const byStream = summary?.collectedByStreamAndMethod;
  const patientWise = summary?.patientWiseCollected ?? [];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            Day-end reconciliation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-4">
            <Label>Date</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-40"
            />
          </div>
          {summaryLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : summary ? (
            <>
              <p className="text-sm text-muted-foreground">
                <strong>Revenue generated</strong> = billed / IPD services dated this day.{" "}
                <strong>Revenue collected</strong> = payments and IPD deposits/refunds posted this day (may differ from
                generated).
              </p>

              <div className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-lg border p-4 space-y-3">
                  <h3 className="font-semibold text-sm">Revenue generated (by stream)</h3>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell>OPD (excl. lab lines &amp; day care)</TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {formatInr(gen?.opd)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Lab (OPD bill lab lines)</TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {formatInr(gen?.lab)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>IPD (bill lines with service date today)</TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {formatInr(gen?.ipd)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Day care</TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {formatInr(gen?.dayCare)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Additional Services</TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {formatInr(gen?.additionalServices)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-semibold">Total generated</TableCell>
                        <TableCell className="text-right font-semibold tabular-nums">
                          {formatInr(gen?.total)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                <div className="rounded-lg border p-4 space-y-3">
                  <h3 className="font-semibold text-sm">Revenue collected (by stream)</h3>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell>OPD</TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {formatInr(col?.opd)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Lab</TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {formatInr(col?.lab)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>IPD (deposits − refunds)</TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {formatInr(col?.ipd)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Day care</TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {formatInr(col?.dayCare)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Additional Services</TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {formatInr(col?.additionalServices)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-semibold">Total collected (streams)</TableCell>
                        <TableCell className="text-right font-semibold tabular-nums">
                          {formatInr(col?.total)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-lg border p-4">
                  <p className="text-sm text-muted-foreground">Total received (all methods)</p>
                  <p className="text-xl font-semibold tabular-nums">{formatInr(summary.totalReceived)}</p>
                  <p className="text-xs text-muted-foreground mt-1">Cash inflow from billing &amp; IPD (non-cash methods separate).</p>
                </div>
                <div className="rounded-lg border p-4">
                  <p className="text-sm text-muted-foreground">Total refunded</p>
                  <p className="text-xl font-semibold tabular-nums text-amber-600">
                    {formatInr(summary.totalRefunded)}
                  </p>
                </div>
                <div className="rounded-lg border p-4">
                  <p className="text-sm text-muted-foreground">Cash expense payouts</p>
                  <p className="text-xl font-semibold tabular-nums text-amber-700">
                    {formatInr(summary.expenseCashOutflow ?? 0)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Receptionist-created patient + central expenses marked <strong>paid in cash</strong> with{" "}
                    <strong>expense date</strong> on this day (
                    {summary.expenseCashOutflowCount ?? 0} line{summary.expenseCashOutflowCount === 1 ? "" : "s"}).
                  </p>
                </div>
                <div className="rounded-lg border p-4">
                  <p className="text-sm text-muted-foreground">Expected cash in drawer</p>
                  <p className="text-xl font-semibold tabular-nums">{formatInr(summary.expectedCash)}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    CASH received − CASH refunds − cash expense payouts above.
                  </p>
                </div>
              </div>

              <div className="rounded-lg border p-4 space-y-2">
                <h3 className="font-semibold text-sm">All payment methods (hospital-wide)</h3>
                <MethodTable rows={summary.byMethod ?? []} emptyHint="No bill payments or IPD movements this day." />
              </div>

              <div className="rounded-lg border p-4 space-y-2">
                <h3 className="font-semibold text-sm">Patient-wise collections (this day)</h3>
                {!patientWise.length ? (
                  <p className="text-sm text-muted-foreground py-2">No patient-wise collections for this day.</p>
                ) : (
                  <div className="overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Patient</TableHead>
                          <TableHead>UHID</TableHead>
                          <TableHead>Phone</TableHead>
                          <TableHead className="text-right">Received</TableHead>
                          <TableHead className="text-right">Refunded</TableHead>
                          <TableHead className="text-right">Net</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {patientWise.slice(0, 200).map((p) => (
                          <TableRow key={p.patientId}>
                            <TableCell className="font-medium">{p.patientName}</TableCell>
                            <TableCell className="text-xs font-mono">{p.uhid ?? "—"}</TableCell>
                            <TableCell className="text-xs">{p.phone ?? "—"}</TableCell>
                            <TableCell className="text-right tabular-nums">{formatInr(p.received)}</TableCell>
                            <TableCell className="text-right tabular-nums text-amber-700">{formatInr(p.refunded)}</TableCell>
                            <TableCell className="text-right tabular-nums font-semibold">{formatInr(p.net)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {patientWise.length > 200 && (
                      <p className="text-xs text-muted-foreground mt-2">Showing first 200 patients.</p>
                    )}
                  </div>
                )}
              </div>

              {byStream && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm">Collected by stream × method</h3>
                  <div className="grid gap-4 md:grid-cols-2">
                    {(Object.keys(STREAM_LABELS) as (keyof StreamMethodBreakdown)[]).map((key) => (
                      <div key={key} className="rounded-lg border p-4 space-y-2">
                        <h4 className="text-sm font-medium">{STREAM_LABELS[key]}</h4>
                        <MethodTable rows={byStream[key] ?? []} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : null}
          <div className="flex gap-2">
            {canOpen && (
              <Button onClick={() => openMutation.mutate()} disabled={openMutation.isPending}>
                {openMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Open session for this day
              </Button>
            )}
            {canClose && sessionForDate && (
              <Button
                variant="outline"
                onClick={() => setCloseId(sessionForDate.id)}
                disabled={closeMutation.isPending}
              >
                Close session (enter actual cash)
              </Button>
            )}
          </div>
          {closeId && (
            <div className="rounded-lg border p-4 space-y-3 max-w-sm">
              <Label>Actual cash counted</Label>
              <Input
                type="number"
                step="0.01"
                value={actualCash}
                onChange={(e) => setActualCash(e.target.value)}
                placeholder="0.00"
              />
              <Input
                value={closeNotes}
                onChange={(e) => setCloseNotes(e.target.value)}
                placeholder="Notes (optional)"
              />
              <div className="flex gap-2">
                <Button onClick={() => closeMutation.mutate(closeId)} disabled={!actualCash || closeMutation.isPending}>
                  {closeMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Close session
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setCloseId(null);
                    setActualCash("");
                    setCloseNotes("");
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
          {sessionForDate?.closedAt && (
            <p className="text-sm text-muted-foreground">
              Session closed at {format(new Date(sessionForDate.closedAt), "PPp")}
              {sessionForDate.variance != null && (
                <span className={sessionForDate.variance === 0 ? " text-green-600" : " text-amber-600"}>
                  {" "}
                  • Variance: ₹{sessionForDate.variance.toFixed(2)}
                </span>
              )}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
